/**
 * Locations grid view for the GHL Qualifier landing page.
 *
 * The card-equivalent of `QualifierLocationsTable`. Mirrors the layout of
 * the Middleware's card grid in `GHLLocationListPage.tsx` so the two
 * dashboards feel like the same product, while pulling qualifier-specific
 * counts (qualified / converted / in-flight) from the location overview row.
 *
 * Pagination:
 *   - Page size is fixed at 12 (3 columns × 4 rows on lg breakpoints).
 *   - Pagination control sits at the bottom — Previous / Page N of M / Next.
 *   - The parent owns the page index so URL params or analytics can pick it
 *     up later. We just slice the data we render.
 *
 * Per-card token-health badge + Revalidate/Reinstall buttons read the
 * Qualifier's own OAuth health endpoint via `useQualifierTokenHealthMap()`.
 */

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart3,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Activity,
} from 'lucide-react'
import { formatRelativeTime } from '@/lib/ghl-qualifier'
import {
  useQualifierGoogleAdsFlowMap,
  useQualifierTestModeMap,
  type QualifierLocationOverviewRow,
} from '@/hooks/useGHLQualifierDashboard'
import { TokenStatusBadge } from '@/components/ghl-dashboard'
import { QualifierTokenActions } from './QualifierTokenActions'
import { GoogleAdsFlowBadge } from './GoogleAdsFlowBadge'
import { TestModeBadge } from './TestModeBadge'
import {
  useQualifierTokenHealthMap,
  type TokenStatus,
} from '@/hooks/useQualifierTokenHealth'

// ──────────────────────────────────────────────────────────────────────────
// Module-scope constants
// ──────────────────────────────────────────────────────────────────────────

/**
 * Base URL of the GHL Qualifier service. Used to drive the Revalidate /
 * Reinstall actions on each card. If unset, the cards still render and the
 * shield badge degrades to gray ("unknown") — Revalidate/Reinstall buttons
 * hide because the Qualifier component reads the same prop.
 */
const QUALIFIER_URL = import.meta.env.VITE_GHL_QUALIFIER_URL || null

/** Cards per page. Mirrors the Middleware's 3×4 visual density. */
const PAGE_SIZE = 12

// ──────────────────────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────────────────────

interface Props {
  readonly data: readonly QualifierLocationOverviewRow[]
  readonly isLoading?: boolean
  readonly onCardClick: (locationId: string) => void
  /** 0-indexed page. Parent owns the value so it can persist or reset. */
  readonly page: number
  readonly onPageChange: (page: number) => void
}

// ──────────────────────────────────────────────────────────────────────────
// Skeleton — copy of GHLLocationListPage's LocationCardSkeleton on purpose.
// Pre-extracting to a shared module would couple the two pages without
// payoff; this is ~15 lines and easy to keep in sync.
// ──────────────────────────────────────────────────────────────────────────

function LocationCardSkeleton() {
  return (
    <Card className="animate-pulse">
      <CardContent className="pt-6">
        <div className="space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-24" />
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

export function QualifierLocationsCards({
  data,
  isLoading,
  onCardClick,
  page,
  onPageChange,
}: Props) {
  const tokenHealth = useQualifierTokenHealthMap()
  const gaFlow = useQualifierGoogleAdsFlowMap()
  const testMode = useQualifierTestModeMap()

  // Loading state — show a small grid of skeletons so the page doesn't
  // shift dramatically when data arrives.
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <LocationCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const total = data.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  // Clamp page index defensively — when a filter shrinks the list the
  // parent should also reset, but a stale `page` would otherwise render an
  // empty slice. `Math.min` keeps the slice non-empty whenever data exists.
  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * PAGE_SIZE
  const visible = data.slice(start, start + PAGE_SIZE)

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {visible.map((loc) => {
          const locToken = tokenHealth.data?.locations[loc.location_id]
          const tokenStatus: TokenStatus = locToken?.status ?? 'unknown'

          // Mirror the table's precise-count fallback so the card's headline
          // number matches what the table view would show for the same row.
          const qualifiedCount =
            loc.qualified_qualified !== undefined && loc.qualified_qualified !== null
              ? loc.qualified_qualified
              : Math.max(
                  0,
                  loc.qualified_total - loc.qualified_unqualified - loc.qualified_pending
                )

          return (
            <Card
              key={loc.location_id}
              className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
              onClick={() => onCardClick(loc.location_id)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-base truncate" title={loc.location_name}>
                      {loc.location_name}
                    </h3>
                    {loc.last_activity_at ? (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Last activity: {formatRelativeTime(loc.last_activity_at)}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-0.5">No activity yet</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                    {gaFlow.data?.has(loc.location_id) ? <GoogleAdsFlowBadge /> : null}
                    {testMode.data?.has(loc.location_id) ? <TestModeBadge /> : null}
                    <TokenStatusBadge status={tokenStatus} detail={locToken} size="sm" />
                    <QualifierTokenActions
                      locationId={loc.location_id}
                      tokenStatus={tokenStatus}
                      qualifierUrl={QUALIFIER_URL}
                      variant="icon-only"
                    />
                    <ChevronRight className="h-5 w-5 text-muted-foreground/40" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">Qualified:</span>
                    <span className="font-medium">{qualifiedCount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">Converted:</span>
                    <span className="font-medium">
                      {loc.converted_completed.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">In flight:</span>
                    <span className="font-medium">
                      {(loc.qualified_pending + loc.converted_in_progress).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">Total:</span>
                    <span className="font-medium">
                      {(loc.qualified_total + loc.converted_total).toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/*
        Pagination controls. Hidden when everything fits on a single page so
        the bottom of the grid doesn't carry noise. Renders even on the
        single-page boundary case to give operators the count anchor.
      */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 pt-2">
          <p className="text-xs text-muted-foreground">
            Showing{' '}
            <span className="font-medium tabular-nums">{start + 1}</span>
            {'–'}
            <span className="font-medium tabular-nums">
              {Math.min(start + PAGE_SIZE, total)}
            </span>{' '}
            of <span className="font-medium tabular-nums">{total}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className="gap-1"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">
              Page {safePage + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage >= totalPages - 1}
              className="gap-1"
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
